<template>
	<ListPageSearch
		ref="listPageSearch"
		mobile-teleport-target="#list-page-search-container"
		:has-map="false"
		:has-sort="false"
		list-type="region"
	/>
	<div>
		<h1
			v-if="name"
			class="region-list__headline"
		>
			{{ name }} Regionen
		</h1>
		<article
			v-if="storeInitialized"
			class="region-list"
		>
			<aside class="region-list__aside">
				<ListFilters
					list-type="region"
					class="region-list-filters"
				/>
			</aside>
			<section class="region-list__content">
				<div class="region-list__list">
					<FilterTagList
						:filter-number="filtersCount"
						class="region-list__filter-tag-list"
					/>
					<div
						v-if="loading"
						class="region-list__load-box"
					>
						<Loading class="region-list__loader" />
					</div>
					<NoOffersFound
						v-else-if="!items.length"
						class="region-list__no-offers-found"
						@OpenFilters="openFilters"
						@OpenSearch="openSearch"
					/>
					<CountryRegionItemList
						v-else
						:items="items"
						class="region-list__item-list"
					/>
				</div>
			</section>
		</article>
	</div>
</template>

<script lang="ts" setup>
import CountryRegionItemList from '@lmt-rpb/CountryRegionItemList/CountryRegionItemList.vue';
import ListFilters from '@lmt-rpb/ListFilters/ListFilters.vue';
import NoOffersFound from '@lmt-rpb/NoOffersFound/NoOffersFound.vue';
import FilterTagList from '@lmt-rpb/FilterTagList/FilterTagList.vue';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import type { AxiosError } from 'axios';
import { useStore } from '@/components/common/store';
import type { SearchFormDataType } from '@interfaces/search-form';
import { updateBreadcrumbUrl } from '@modules/updateUrls';
import { EventBus } from '@global-js/event-bus';
import { getAll as getAllParams, updateURL } from '@components/common/services/url';
import ListPageSearch from '@lmt-rpb/ListPageSearch/ListPageSearch.vue';
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import * as searchHistoryService from '@components/common/services/localStorage/searchHistoryService';
import type { BestOfferForRegion } from '@/interfaces/api/v1-best-offer-for-region';
import { formToParams } from '@services/transform';
import { fetchTourOperatorInventory } from '@services/tourOperatorInventoryService';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import { fetchItems } from '@services/bestOfferForRegionService';
import { useFilters } from 'src/composables/useFIlters';

const store = useStore();

interface Props {
	displaymode: string;
	rlId: string;
	countryCode: string;
}

const props = defineProps<Props>();

const listPageSearch = ref<InstanceType<typeof ListPageSearch>>();

const ratingAttributes = ref('');

const items = ref<BestOfferForRegion[]>([]);

const formData = computed((): SearchFormDataType => store.state.searchMask);

const name = ref(formData.value.destination?.label);

const loading = ref(true);

const redirectTriggered = ref(false);

const error = ref<AxiosError | null>(null);

const savedFormData = ref<SearchFormDataType>({ ...formData.value });

const storeInitialized = ref(store.state.config.storeInitialized);

const { isDesktop } = storeToRefs(useBreakpointStore());

const { filtersCount } = useFilters();

// save list before navigating away
const rememberRegionListItems = (): void => {
	// prevent getting empty NodeList if setting new destination in searchmask
	// (if loading true --> regionList not rendered)
	if (loading.value || redirectTriggered.value) {
		return;
	}

	redirectTriggered.value = true;

	const openRegionNodeList = document.querySelectorAll('.collapse.country-item-list__collapse.is-open');
	const openRegionsArray: string[] = [];

	if (openRegionNodeList) {
		openRegionNodeList.forEach((openRegionNode) => {
			const nodeAttribute = (openRegionNode as HTMLElement).getAttribute('data-region');

			if (nodeAttribute) {
				openRegionsArray.push(nodeAttribute);
			}
		});
	}

	const sessionArray = [Date.now(), window.scrollY, items.value, openRegionsArray];
	try {
		sessionStorage.setItem(window.location.href, JSON.stringify(sessionArray));
	} catch (errorSetItem) {
		console.log(errorSetItem);
	}
};

const getItems = (): void => {
	// Saving regionlist items in case of redirect
	// (RegionList loading is triggered on redirect and empty)
	loading.value = true;
	rememberRegionListItems();
	const bodyParams = {
		displaymode: props.displaymode,
		countryCode: props.countryCode,
		rlId: props.rlId,
	};
	fetchItems(formData.value.onlyHotel, bodyParams).then((resp) => {
		loading.value = resp.loading;
		error.value = resp.error;
		if (!resp.data) {
			items.value = [];
			return;
		}
		items.value = resp.data;
	});
};

const fetchTourOperators = (): void => {
	const bodyParams = {
		locationId: props.displaymode === 'country' ? [props.countryCode] : [],
		regionGroupIds: props.displaymode === 'regiongroup' ? [parseInt(props.rlId, 10)] : [],
	};
	fetchTourOperatorInventory(bodyParams, ['DealsOnly']);
};

const formDataWasChanged = (): boolean => {
	// Excludes for offerlist update

	const {
		/* eslint-disable @typescript-eslint/no-unused-vars */
		destination: currentDestination,
		searchTerm: currentSearchTerm,
		modified: currentModified,
		maxTravelDurationError: currentError,
		...newFormData
	} = formData.value;

	const {
		destination: savedDestination,
		searchTerm: savedtSearchTerm,
		modified: savedModified,
		maxTravelDurationError: savedError,
		/* eslint-enable @typescript-eslint/no-unused-vars */
		...newSavedFormData
	} = savedFormData.value;

	// Update savedFormData
	savedFormData.value = { ...formData.value };

	if (newFormData.additionalCheckboxFilters?.ratingAttributes) {
		const newRatingAttributes = JSON.stringify(
			newFormData.mostPopularFilters?.ratingAttributes?.concat(newFormData.additionalCheckboxFilters?.ratingAttributes)
		);
		// Check if wanted fields updated
		if (newRatingAttributes !== ratingAttributes.value) {
			ratingAttributes.value = newRatingAttributes;
			return true;
		}
	}

	return JSON.stringify(newFormData) !== JSON.stringify(newSavedFormData);
};

const handleMobileSubmit = (): void => {
	if (!isDesktop.value && formDataWasChanged()) {
		searchHistoryService.update();
		updateURL(formToParams(formData.value, 'regionList'));
		getItems();
		fetchTourOperators();
	}
};

const preventScrollRestoration = (): void => {
	if (window.history.scrollRestoration) {
		window.history.scrollRestoration = 'manual';
	}
};

onBeforeMount((): void => {
	// update query params if page is loaded with params
	const params = getAllParams();
	if (Object.keys(params).length) {
		updateURL(formToParams(formData.value, 'regionList'));
	}
	window.addEventListener('popstate', handlePopState);

	window.addEventListener('pagehide', rememberRegionListItems.bind(this));
});

const handlePopState = ({ state }: PopStateEvent): void => {
	EventBus.$emit('window:popstate', state);
};

const getRegionElement = (regionName: string): HTMLElement | null =>
	document.querySelector(`.collapse.country-item-list__collapse[data-region="${regionName}"] .collapse__header`);

function openSearch() {
	if (!isDesktop.value) {
		listPageSearch.value!.searchFormModal!.openModal();
	}
}

function openFilters() {
	if (!isDesktop.value) {
		listPageSearch.value!.filterModal!.openModal();
	}
}

onMounted((): void => {
	let pageReloaded = true;

	if (!performance.navigation) {
		console.warn('performance.navigation isnt supported by this browser');
	}

	// Once this stops working, we need to find another solution like using
	// https://developer.mozilla.org/en-US/docs/Web/API/PerformanceNavigationTiming
	if (performance.navigation.type !== PerformanceNavigation.TYPE_RELOAD) {
		pageReloaded = false;
	}

	// fetch items
	try {
		const lastSessionArray = JSON.parse(sessionStorage.getItem(window.location.href) as string);
		if (lastSessionArray && Date.now() < lastSessionArray[0] + 3600000 && !pageReloaded) {
			// 3600000 is 1 hour in ms
			if (history.scrollRestoration) {
				history.scrollRestoration = 'manual';
			}

			items.value = lastSessionArray[2];
			loading.value = false;

			const savedOpenRegions = lastSessionArray[3];
			const firstItemRegionName = lastSessionArray[2] && lastSessionArray[2][0] ? lastSessionArray[2][0].RegionGroupName : false;

			nextTick(() => {
				savedOpenRegions.forEach((region: string) => {
					// prevent double opening first item
					if (firstItemRegionName && region !== firstItemRegionName) {
						const openRegion = getRegionElement(region);
						(openRegion as HTMLElement)?.click();
					}
				});

				// check if first element should be closed
				if (firstItemRegionName && firstItemRegionName !== savedOpenRegions[0]) {
					const firstItemElement = getRegionElement(firstItemRegionName);

					if (firstItemElement) {
						(firstItemElement as HTMLElement).click();
					}
				}

				// fix for wrong scrolling
				setTimeout(() => {
					window.scrollTo(0, lastSessionArray[1]);
				}, 750);
			});
		} else {
			// prevent using old scroll position
			preventScrollRestoration();
			getItems();
		}
	} catch {
		// prevent using old scroll position
		preventScrollRestoration();
		getItems();
	}

	fetchTourOperators();
	EventBus.$on('SearchMask:MobileSubmit', handleMobileSubmit);

	if (formData.value.additionalCheckboxFilters?.ratingAttributes) {
		ratingAttributes.value = JSON.stringify(
			formData.value.mostPopularFilters?.ratingAttributes?.concat(formData.value.additionalCheckboxFilters?.ratingAttributes)
		);
	}

	// Update breadcrumb URLs
	EventBus.$on('url:change', updateBreadcrumbUrl);
	// Initial update of URLs
	updateBreadcrumbUrl();
});

onBeforeUnmount((): void => {
	window.removeEventListener('popstate', handlePopState);
	EventBus.$off('SearchMask:MobileSubmit', handleMobileSubmit);
	EventBus.$off('url:change', updateBreadcrumbUrl);

	window.removeEventListener('pagehide', rememberRegionListItems.bind(this));
});

watch(
	() => formData.value,
	() => {
		if (!isDesktop.value) {
			return;
		}
		if (formDataWasChanged()) {
			searchHistoryService.update();
			updateURL(formToParams(formData.value, 'regionList'));
			getItems();
		}
	},
	{ deep: true }
);
</script>

<style lang="scss" scoped>
.region-list {
	display: flex;
	flex-direction: column;
	margin: 3rem auto 0;

	&__filter-tag-list {
		margin-bottom: 2.4rem;
	}

	@media (min-width: $breakpoint-small) {
		margin-top: 6rem;
	}

	@media (min-width: $breakpoint-extralarge) {
		margin-top: 3rem;
	}

	scrollbar-color: $color-primary transparent;
	scrollbar-width: thin;
	-webkit-tap-highlight-color: transparent;

	::-webkit-scrollbar {
		width: 1.2rem;
	}

	::-webkit-scrollbar-thumb {
		border-radius: $border-radius-small;
		background-color: $color-primary;
	}

	&__headline {
		text-align: center;
		padding-top: 3rem;
		padding-bottom: 0;
	}

	&__aside {
		display: none;
	}

	.region-list-filters {
		max-width: 35rem;
	}

	&__list-sort-filters {
		display: flex;
		gap: 1rem;

		@media screen and (min-width: $breakpoint-mobilelarge) {
			margin: 0 auto;
		}

		@media screen and (min-width: $breakpoint-large) {
			margin: 0 auto;
		}

		@media screen and (min-width: $breakpoint-extralarge) {
			margin: 0;
		}

		&--no-results {
			margin: 0;
		}
	}

	&__list {
		position: relative;
		z-index: 0;
	}

	&__no-results {
		margin-top: 5.3rem;
		margin-left: 0.36rem;
	}

	.hotelrating .hotelrating__icon path {
		fill: unset;
	}

	&__loader {
		margin: 30rem auto;
	}

	&__no-offers-found {
		margin-top: 5.3rem;
	}

	@include media-query-up($breakpoint-large) {
		flex-direction: row;

		&__content {
			width: 100%;
			margin-left: 0;
		}
	}

	@include media-query-up($breakpoint-extralarge) {
		flex-direction: row;
		max-width: 144rem;

		&__headline {
			text-align: left;
			padding-left: 0;
			padding-right: 0;
			max-width: 144rem;
			margin-right: auto;
			margin-left: auto;
			border-bottom: 0.1rem solid $color-lime-light;
		}

		&__aside {
			width: 25%;
			display: block;
			min-width: 35rem;
		}

		&__content {
			min-width: 70%;
			margin-left: 2.5rem;
		}
	}
}
</style>
